
import { defineComponent, PropType } from 'vue'

import {
  IPinnableReportItem,
  PinnableReportType,
  ReportMeta,
  ReportPageT
} from '~/types/reportTypes'
import { PinnedReport, ReportLevel, ReportType } from '~/types/types'

interface IReportsList {
  title: 'SECURITY AND COMPLIANCE' | 'INSIGHTS'
  type: ReportType
  items: Array<IPinnableReportItem>
}

interface IZMenu extends Vue {
  close(): () => void
}

export default defineComponent({
  name: 'PinnableReportsList',
  props: {
    currentSelection: {
      type: String,
      required: true
    },
    level: {
      type: String as PropType<ReportLevel>,
      required: true
    },
    owner: {
      type: String,
      required: true
    },
    pinnedReports: {
      type: Array as PropType<Array<PinnedReport>>,
      required: true
    },
    provider: {
      type: String,
      required: true
    },
    repositoryGroupSlug: String,
    repoName: String,
    reportSlot: {
      type: Number,
      required: true
    }
  },
  emits: ['update-pinned-reports'],
  data() {
    return {
      collapsibleHeaderAnimations: {
        [ReportPageT.DISTRIBUTION]: '',
        [ReportPageT.ISSUES_PREVENTED]: '',
        [ReportPageT.ACTIVE_THIRD_PARTY_VULNERABILITIES]: ''
      } as Record<ReportPageT, string>,
      collapsibleVisibilityStatus: {
        [ReportPageT.DISTRIBUTION]: false,
        [ReportPageT.ISSUES_PREVENTED]: false,
        [ReportPageT.ACTIVE_THIRD_PARTY_VULNERABILITIES]: false
      } as Record<ReportPageT, boolean>,
      PinnableReportType,
      ReportType
    }
  },
  mounted(): void {
    // Expand the corresponding collapsible with the selected item `By Analyzer/Category`
    this.expandCurrentlySelectedCollapsible()
  },
  computed: {
    complianceItems(): Array<IPinnableReportItem> {
      const complianceItems = Object.keys(ReportMeta)
        .filter((reportKey) => {
          const { level, type } = ReportMeta[reportKey as ReportPageT]
          return level.includes(this.level) && type === ReportType.Compliance
        })
        .map((reportKey) => {
          const { pinnableType, title } = ReportMeta[reportKey as ReportPageT]

          return {
            key: reportKey,
            label: title,
            metadata: null,
            type: pinnableType // `compliance`
          } as IPinnableReportItem
        })

      return complianceItems
    },

    cookieIdentifier(): string {
      const reportItem = this.pinnedReports[this.reportSlot]

      if (reportItem?.key === ReportPageT.CODE_COVERAGE) {
        return `${this.provider}_${this.owner}_pinned-code-coverage-sort-type_${this.reportSlot}`
      }

      // Cookie identifier has the convention `provider_owner_repo_reportType_date-range-filter_report-slot`
      // For groups: `provider_owner_group_groupSlug_reportType_date-range-filter_report-slot`
      const reportType = reportItem?.metadata?.filter || reportItem?.key

      let cookieIdentifier = `${this.provider}_${this.owner}`

      if (this.level === ReportLevel.Repository) {
        cookieIdentifier += `_${this.repoName}`
      } else if (this.level === ReportLevel.RepositoryGroup) {
        cookieIdentifier += `_group_${this.repositoryGroupSlug}`
      }

      return `${cookieIdentifier}_${reportType}_date-range-filter_${this.reportSlot}`
    },

    expandToTop(): boolean {
      return this.reportSlot > 1
    },

    insightsItems(): Array<IPinnableReportItem> {
      const insightsItems = Object.keys(ReportMeta)
        .filter((reportKey) => {
          const { level, type } = ReportMeta[reportKey as ReportPageT]
          return level.includes(this.level) && type === ReportType.Insight
        })
        .map((reportKey) => {
          const { metadataItems, pinnableType, title } = ReportMeta[reportKey as ReportPageT]
          const metadata = metadataItems ? { metadataItems } : { metadata: null }

          const item = {
            key: reportKey,
            label: title,
            type: pinnableType, // `distribution` & `non-distribution`
            ...metadata
          } as IPinnableReportItem

          return item
        })

      return insightsItems
    },

    reportsList(): Array<IReportsList> {
      const reportsList = [
        {
          title: 'SECURITY AND COMPLIANCE',
          type: ReportType.Compliance,
          items: [...this.complianceItems]
        },
        { title: 'INSIGHTS', type: ReportType.Insight, items: [...this.insightsItems] }
      ] as Array<IReportsList>

      return reportsList
    }
  },
  methods: {
    closeMenu(): void {
      const menuContainer = this.$refs['menu-container'] as IZMenu
      menuContainer.close()
    },

    expandCurrentlySelectedCollapsible(): void {
      this.insightsItems.forEach((item) => {
        if (item.type === PinnableReportType.DISTRIBUTION) {
          const isCurrentlyPinned = this.isCurrentlyPinnedReport(item as IPinnableReportItem)

          this.collapsibleVisibilityStatus[item.key] = isCurrentlyPinned
          this.collapsibleHeaderAnimations[item.key] = isCurrentlyPinned ? 'rotate-180' : 'rotate-0'
        }
      })
    },

    isCurrentlyPinnedReport(reportItem: IPinnableReportItem): boolean {
      if (reportItem.metadata) {
        return reportItem.metadata.filter === this.currentSelection
      }

      if (reportItem.metadataItems) {
        return this.currentSelection.startsWith(reportItem.key)
      }

      return reportItem.key === this.currentSelection
    },

    menuToggleHandler(isOpen: boolean): void {
      if (!isOpen) {
        return
      }

      Object.keys(this.collapsibleHeaderAnimations).forEach((key) => {
        this.collapsibleHeaderAnimations[key as ReportPageT] = ''
      })

      this.expandCurrentlySelectedCollapsible()
    },

    updateCollapsibleMetadata(reportItem: IPinnableReportItem): void {
      this.collapsibleHeaderAnimations[reportItem.key] = this.collapsibleVisibilityStatus[
        reportItem.key
      ]
        ? 'animate-reverse-half-spin rotate-0'
        : 'animate-first-half-spin rotate-180'

      const collapsibleIsOpen = !this.collapsibleVisibilityStatus[reportItem.key]

      this.collapsibleVisibilityStatus[reportItem.key] = collapsibleIsOpen

      Object.keys(this.collapsibleVisibilityStatus).forEach((key) => {
        if (key !== reportItem.key) {
          const alternativeCollapsibleIsOpen = this.collapsibleVisibilityStatus[key as ReportPageT]

          if (collapsibleIsOpen && alternativeCollapsibleIsOpen) {
            this.collapsibleVisibilityStatus[key as ReportPageT] = !collapsibleIsOpen
            this.collapsibleHeaderAnimations[key as ReportPageT] =
              'animate-reverse-half-spin rotate-0'
          }
        }
      })
    },

    updatePinnedReport(reportItem: IPinnableReportItem): void {
      this.closeMenu()

      if (this.isCurrentlyPinnedReport(reportItem)) {
        return
      }

      this.$cookies.remove(this.cookieIdentifier)

      const { key, metadata } = reportItem
      const pinnedReports = [...this.pinnedReports]

      pinnedReports[this.reportSlot] = { key, metadata }
      this.$root.$emit('update-pinned-reports', pinnedReports, this.reportSlot)
    }
  }
})
